import React from 'react';
import { Dropdown, type MenuProps } from 'antd';
import {
  AlignedInfo,
  Box,
  Button,
  DateWithoutTimeZone,
  Flex,
  Gap,
  Icon,
  Modal,
  Text,
} from '@common/components';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { DELETE_WORKER_PROFILE_CHANGE_MUTATION } from '@load-balancing/graphql/mutations/delete-worker-profile-change';
import {
  fteHoursLabel,
  shiftPatternLabel,
  shiftPreferenceLabel,
  type TeamReviewChange,
} from '@load-balancing/services';

// Shared "Upcoming Changes" list (WORK-6258), rendered in both the in-session profile panel
// and the Team Review worker drawer so the two surfaces stay identical. Each card has an
// Edit / Delete menu: Edit defers to the host (which opens the edit form), Delete is owned
// here (confirm + mutation + toast + refetch) so both surfaces behave the same.
export function ScheduleWorkerUpcomingChanges({
  changes,
  workerId,
  schedulingPeriodId,
  onEdit,
}: {
  changes: TeamReviewChange[];
  workerId: string;
  schedulingPeriodId?: string | null;
  onEdit: (changeId: string) => void;
}) {
  const [deleteChange] = useMutation(DELETE_WORKER_PROFILE_CHANGE_MUTATION, {
    refetchQueries: ['LoadBalancingWorker', 'ScheduleReview'],
    // errorPolicy is 'all', so GraphQL errors resolve here with no worker (rather than
    // rejecting). Only toast success when the delete actually returned a worker.
    onCompleted: (data) => {
      if (!data?.deleteWorkerProfileChange?.worker) {
        createMessage(
          'error',
          'Could not delete the change. Please try again.'
        );
        return;
      }
      createMessage('success', 'Scheduled change deleted.');
    },
    onError: () =>
      createMessage('error', 'Could not delete the change. Please try again.'),
  });

  const handleDelete = (changeId: string) =>
    confirmDeleteChange(() =>
      deleteChange({
        variables: { workerId, termId: changeId, schedulingPeriodId },
      })
    );

  if (changes.length === 0) {
    return <Text secondary>No upcoming changes.</Text>;
  }

  return (
    <Gap
      vertical
      size="md"
      align="stretch"
      className="schedule-worker-upcoming-change"
    >
      {changes.map((change) => (
        <Box key={change.id} color="steelLightest">
          <Gap vertical size="xxs">
            <Flex justify="space-between" align="start" fullWidth>
              <Text weight="medium" block>
                Effective{' '}
                <DateWithoutTimeZone date={change.effectiveStartDate} />
                {change.effectiveEndDate && (
                  <>
                    – <DateWithoutTimeZone date={change.effectiveEndDate} />
                  </>
                )}
              </Text>
              <Dropdown
                trigger={['click']}
                menu={{ items: actionItems(change.id, onEdit, handleDelete) }}
              >
                <Button kind="link" size="sm" ariaLabel="More actions">
                  <Icon icon="verticalEllipsis" kind="regular" size="lg" fit />
                </Button>
              </Dropdown>
            </Flex>
            <AlignedInfo
              fullWidth
              content={[
                ['Role', change.role ?? '—'],
                ['FTE Hours', fteHoursLabel(change.fteHours)],
                [
                  'Shift Preference',
                  shiftPreferenceLabel(change.preferredTime),
                ],
                [
                  'Shift Pattern',
                  shiftPatternLabel(change.shiftPatternPreference),
                ],
                ['Charge Eligible', change.chargeEligible ? 'Yes' : '—'],
                ['Status', change.active ? 'Active' : 'Inactive'],
              ]}
            />
          </Gap>
        </Box>
      ))}
    </Gap>
  );
}

function actionItems(
  changeId: string,
  onEdit: (changeId: string) => void,
  onDelete: (changeId: string) => void
): NonNullable<MenuProps['items']> {
  return [
    { key: 'edit', label: 'Edit', onClick: () => onEdit(changeId) },
    {
      key: 'delete',
      label: 'Delete',
      danger: true,
      onClick: () => onDelete(changeId),
    },
  ];
}

// Opens the "delete scheduled change" confirmation; the caller supplies what runs on confirm.
function confirmDeleteChange(onConfirm: () => void) {
  Modal.confirm({
    title: 'Delete scheduled change?',
    content: `This scheduled change will be removed. The previous profile will extend forward to keep the timeline continuous. Existing shifts will not be automatically updated, and schedule metrics may change.`,
    okText: 'Delete',
    danger: true,
    onOk: onConfirm,
  });
}
